import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import type { POLineItemCreatePayload, POLineItemUpdatePayload, PurchaseOrder, PurchaseOrderCreatePayload, PurchaseOrderUpdatePayload } from "../../services/purchaseOrderService";
import {
    addPOLineItem as addPOLineItemApi,
    approvePurchaseOrder as approvePurchaseOrderApi,
    cancelPurchaseOrder as cancelPurchaseOrderApi,
    createPurchaseOrder as createPurchaseOrderApi,
    deletePOLineItem as deletePOLineItemApi,
    deletePurchaseOrder as deletePurchaseOrderApi,
    getPurchaseOrderById,
    getPurchaseOrders,
    receivePurchaseOrder as receivePurchaseOrderApi,
    updatePOLineItem as updatePOLineItemApi,
    updatePurchaseOrder as updatePurchaseOrderApi,
} from "../../services/purchaseOrderService";

export const initialState = {
    purchaseOrders: [] as PurchaseOrder[],
    loading: false,
    error: null as any,
    selectedPurchaseOrder: null as PurchaseOrder | null,
};

export const fetchPurchaseOrders = createAsyncThunk(
    "purchaseOrders/fetchPurchaseOrders",
    async ({ pageNumber = 1, pageSize = 50, status }: { pageNumber?: number; pageSize?: number; status?: number } = {}, { rejectWithValue }) => {
        try {
            const response = await getPurchaseOrders(pageNumber, pageSize, status);
            return response;
        } catch (error: any) {
            return rejectWithValue(error);
        }
    }
);

export const fetchPurchaseOrderById = createAsyncThunk(
    "purchaseOrders/fetchPurchaseOrderById",
    async (id: string, { rejectWithValue }) => {
        try {
            const response = await getPurchaseOrderById(id);
            return response;
        } catch (error: any) {
            return rejectWithValue(error);
        }
    }
);

export const createPurchaseOrder = createAsyncThunk(
    "purchaseOrders/createPurchaseOrder",
    async (po: PurchaseOrderCreatePayload, { rejectWithValue }) => {
        try {
            const poId = await createPurchaseOrderApi(po);
            // Fetch the created PO to get full details
            const response = await getPurchaseOrderById(poId);
            return response;
        } catch (error: any) {
            return rejectWithValue(error);
        }
    }
);

export const updatePurchaseOrder = createAsyncThunk(
    "purchaseOrders/updatePurchaseOrder",
    async ({ id, data }: { id: string; data: PurchaseOrderUpdatePayload }, { rejectWithValue }) => {
        try {
            await updatePurchaseOrderApi(id, data);
            // Fetch updated PO to get full details
            const response = await getPurchaseOrderById(id);
            return response;
        } catch (error: any) {
            return rejectWithValue(error);
        }
    }
);

export const deletePurchaseOrder = createAsyncThunk(
    "purchaseOrders/deletePurchaseOrder",
    async (id: string, { rejectWithValue }) => {
        try {
            await deletePurchaseOrderApi(id);
            return id;
        } catch (error: any) {
            return rejectWithValue(error);
        }
    }
);

export const approvePurchaseOrder = createAsyncThunk(
    "purchaseOrders/approvePurchaseOrder",
    async (id: string, { rejectWithValue }) => {
        try {
            await approvePurchaseOrderApi(id);
            // Fetch updated PO to get full details
            const response = await getPurchaseOrderById(id);
            return response;
        } catch (error: any) {
            // Extract error message from API response
            const errorMessage = error?.response?.data?.message
                || error?.message
                || "Failed to approve purchase order";
            return rejectWithValue(errorMessage);
        }
    }
);

export const receivePurchaseOrder = createAsyncThunk(
    "purchaseOrders/receivePurchaseOrder",
    async (id: string, { rejectWithValue }) => {
        try {
            await receivePurchaseOrderApi(id);
            // Fetch updated PO to get full details
            const response = await getPurchaseOrderById(id);
            return response;
        } catch (error: any) {
            // Extract error message from API response
            const errorMessage = error?.response?.data?.message
                || error?.message
                || "Failed to receive purchase order";
            return rejectWithValue(errorMessage);
        }
    }
);

export const cancelPurchaseOrder = createAsyncThunk(
    "purchaseOrders/cancelPurchaseOrder",
    async (id: string, { rejectWithValue }) => {
        try {
            await cancelPurchaseOrderApi(id);
            // Fetch updated PO to get full details
            const response = await getPurchaseOrderById(id);
            return response;
        } catch (error: any) {
            // Extract error message from API response
            const errorMessage = error?.response?.data?.message
                || error?.message
                || "Failed to cancel purchase order";
            return rejectWithValue(errorMessage);
        }
    }
);

export const addPOLineItem = createAsyncThunk(
    "purchaseOrders/addPOLineItem",
    async ({ purchaseOrderId, lineItem }: { purchaseOrderId: string; lineItem: POLineItemCreatePayload }, { rejectWithValue }) => {
        try {
            await addPOLineItemApi(purchaseOrderId, lineItem);
            // Fetch updated PO to get full details
            const response = await getPurchaseOrderById(purchaseOrderId);
            return response;
        } catch (error: any) {
            return rejectWithValue(error);
        }
    }
);

export const updatePOLineItem = createAsyncThunk(
    "purchaseOrders/updatePOLineItem",
    async ({ id, purchaseOrderId, lineItem }: { id: string; purchaseOrderId: string; lineItem: POLineItemUpdatePayload }, { rejectWithValue }) => {
        try {
            await updatePOLineItemApi(id, lineItem);
            // Fetch updated PO to get full details
            const response = await getPurchaseOrderById(purchaseOrderId);
            return response;
        } catch (error: any) {
            return rejectWithValue(error);
        }
    }
);

export const deletePOLineItem = createAsyncThunk(
    "purchaseOrders/deletePOLineItem",
    async ({ id, purchaseOrderId }: { id: string; purchaseOrderId: string }, { rejectWithValue }) => {
        try {
            await deletePOLineItemApi(id);
            // Fetch updated PO to get full details
            const response = await getPurchaseOrderById(purchaseOrderId);
            return response;
        } catch (error: any) {
            return rejectWithValue(error);
        }
    }
);

const purchaseOrderSlice = createSlice({
    name: "purchaseOrders",
    initialState,
    reducers: {
        selectPurchaseOrder(state: any, action: PayloadAction<string>) {
            state.selectedPurchaseOrder =
                state.purchaseOrders.find((po: PurchaseOrder) => po.id === action.payload) || null;
        },
        clearError(state: any) {
            state.error = null;
        },
    },
    extraReducers: (builder) => {
        // Fetch List
        builder.addCase(fetchPurchaseOrders.pending, (state: any) => {
            state.loading = true;
            state.error = null;
        });
        builder.addCase(fetchPurchaseOrders.fulfilled, (state: any, action: any) => {
            state.loading = false;
            // Handle PaginatedResponse structure from v2.0 API
            if (action.payload && action.payload.items) {
                state.purchaseOrders = Array.isArray(action.payload.items) ? action.payload.items : [];
            } else {
                // Fallback for array response (backward compatibility)
                state.purchaseOrders = Array.isArray(action.payload) ? action.payload : [];
            }
        });
        builder.addCase(fetchPurchaseOrders.rejected, (state: any, action: any) => {
            state.loading = false;
            state.error = action.payload?.message || action.payload || "Failed to fetch purchase orders";
        });

        // Fetch By ID
        builder.addCase(fetchPurchaseOrderById.pending, (state: any) => {
            state.loading = true;
            state.error = null;
        });
        builder.addCase(fetchPurchaseOrderById.fulfilled, (state: any, action: any) => {
            state.loading = false;
            const po = action.payload;
            if (po && po.id) {
                const index = state.purchaseOrders.findIndex((item: PurchaseOrder) => item.id === po.id);
                if (index !== -1) {
                    state.purchaseOrders[index] = po;
                } else {
                    state.purchaseOrders.push(po);
                }
                state.selectedPurchaseOrder = po;
            }
        });
        builder.addCase(fetchPurchaseOrderById.rejected, (state: any, action: any) => {
            state.loading = false;
            state.error = action.payload?.message || action.payload || "Failed to fetch purchase order";
        });

        // Create
        builder.addCase(createPurchaseOrder.pending, (state: any) => {
            state.loading = true;
            state.error = null;
        });
        builder.addCase(createPurchaseOrder.fulfilled, (state: any, action: any) => {
            state.loading = false;
            const po = action.payload;
            if (po && po.id) {
                const exists = state.purchaseOrders.some((item: PurchaseOrder) => item.id === po.id);
                if (!exists) {
                    state.purchaseOrders.push(po);
                }
                state.selectedPurchaseOrder = po;
            }
        });
        builder.addCase(createPurchaseOrder.rejected, (state: any, action: any) => {
            state.loading = false;
            state.error = action.payload?.message || action.payload || "Failed to create purchase order";
        });

        // Update
        builder.addCase(updatePurchaseOrder.pending, (state: any) => {
            state.loading = true;
            state.error = null;
        });
        builder.addCase(updatePurchaseOrder.fulfilled, (state: any, action: any) => {
            state.loading = false;
            const po = action.payload;
            if (po && po.id) {
                const index = state.purchaseOrders.findIndex((item: PurchaseOrder) => item.id === po.id);
                if (index !== -1) {
                    state.purchaseOrders[index] = po;
                }
                state.selectedPurchaseOrder = po;
            }
        });
        builder.addCase(updatePurchaseOrder.rejected, (state: any, action: any) => {
            state.loading = false;
            state.error = action.payload?.message || action.payload || "Failed to update purchase order";
        });

        // Delete
        builder.addCase(deletePurchaseOrder.pending, (state: any) => {
            state.loading = true;
            state.error = null;
        });
        builder.addCase(deletePurchaseOrder.fulfilled, (state: any, action: any) => {
            state.loading = false;
            state.purchaseOrders = state.purchaseOrders.filter((po: PurchaseOrder) => po.id !== action.payload);
            if (state.selectedPurchaseOrder?.id === action.payload) {
                state.selectedPurchaseOrder = null;
            }
        });
        builder.addCase(deletePurchaseOrder.rejected, (state: any, action: any) => {
            state.loading = false;
            state.error = action.payload?.message || action.payload || "Failed to delete purchase order";
        });

        // Approve
        builder.addCase(approvePurchaseOrder.pending, (state: any) => {
            state.loading = true;
            state.error = null;
        });
        builder.addCase(approvePurchaseOrder.fulfilled, (state: any, action: any) => {
            state.loading = false;
            const po = action.payload;
            if (po && po.id) {
                const index = state.purchaseOrders.findIndex((item: PurchaseOrder) => item.id === po.id);
                if (index !== -1) {
                    state.purchaseOrders[index] = po;
                }
                state.selectedPurchaseOrder = po;
            }
        });
        builder.addCase(approvePurchaseOrder.rejected, (state: any, action: any) => {
            state.loading = false;
            state.error = action.payload || "Failed to approve purchase order";
        });

        // Receive
        builder.addCase(receivePurchaseOrder.pending, (state: any) => {
            state.loading = true;
            state.error = null;
        });
        builder.addCase(receivePurchaseOrder.fulfilled, (state: any, action: any) => {
            state.loading = false;
            const po = action.payload;
            if (po && po.id) {
                const index = state.purchaseOrders.findIndex((item: PurchaseOrder) => item.id === po.id);
                if (index !== -1) {
                    state.purchaseOrders[index] = po;
                }
                state.selectedPurchaseOrder = po;
            }
        });
        builder.addCase(receivePurchaseOrder.rejected, (state: any, action: any) => {
            state.loading = false;
            state.error = action.payload || "Failed to receive purchase order";
        });

        // Cancel
        builder.addCase(cancelPurchaseOrder.pending, (state: any) => {
            state.loading = true;
            state.error = null;
        });
        builder.addCase(cancelPurchaseOrder.fulfilled, (state: any, action: any) => {
            state.loading = false;
            const po = action.payload;
            if (po && po.id) {
                const index = state.purchaseOrders.findIndex((item: PurchaseOrder) => item.id === po.id);
                if (index !== -1) {
                    state.purchaseOrders[index] = po;
                }
                state.selectedPurchaseOrder = po;
            }
        });
        builder.addCase(cancelPurchaseOrder.rejected, (state: any, action: any) => {
            state.loading = false;
            state.error = action.payload || "Failed to cancel purchase order";
        });

        // Add Line Item
        builder.addCase(addPOLineItem.pending, (state: any) => {
            state.loading = true;
            state.error = null;
        });
        builder.addCase(addPOLineItem.fulfilled, (state: any, action: any) => {
            state.loading = false;
            const po = action.payload;
            if (po && po.id) {
                const index = state.purchaseOrders.findIndex((item: PurchaseOrder) => item.id === po.id);
                if (index !== -1) {
                    state.purchaseOrders[index] = po;
                }
                state.selectedPurchaseOrder = po;
            }
        });
        builder.addCase(addPOLineItem.rejected, (state: any, action: any) => {
            state.loading = false;
            state.error = action.payload?.message || action.payload || "Failed to add line item";
        });

        // Update Line Item
        builder.addCase(updatePOLineItem.pending, (state: any) => {
            state.loading = true;
            state.error = null;
        });
        builder.addCase(updatePOLineItem.fulfilled, (state: any, action: any) => {
            state.loading = false;
            const po = action.payload;
            if (po && po.id) {
                const index = state.purchaseOrders.findIndex((item: PurchaseOrder) => item.id === po.id);
                if (index !== -1) {
                    state.purchaseOrders[index] = po;
                }
                state.selectedPurchaseOrder = po;
            }
        });
        builder.addCase(updatePOLineItem.rejected, (state: any, action: any) => {
            state.loading = false;
            state.error = action.payload?.message || action.payload || "Failed to update line item";
        });

        // Delete Line Item
        builder.addCase(deletePOLineItem.pending, (state: any) => {
            state.loading = true;
            state.error = null;
        });
        builder.addCase(deletePOLineItem.fulfilled, (state: any, action: any) => {
            state.loading = false;
            const po = action.payload;
            if (po && po.id) {
                const index = state.purchaseOrders.findIndex((item: PurchaseOrder) => item.id === po.id);
                if (index !== -1) {
                    state.purchaseOrders[index] = po;
                }
                state.selectedPurchaseOrder = po;
            }
        });
        builder.addCase(deletePOLineItem.rejected, (state: any, action: any) => {
            state.loading = false;
            state.error = action.payload?.message || action.payload || "Failed to delete line item";
        });
    },
});

export const { selectPurchaseOrder, clearError } = purchaseOrderSlice.actions;

export const selectPurchaseOrderList = (state: any) => state.PurchaseOrders.purchaseOrders;
export const selectPurchaseOrderById = (state: any, id: string) =>
    state.PurchaseOrders.purchaseOrders.find((po: PurchaseOrder) => po.id === id);
export const selectSelectedPurchaseOrder = (state: any) => state.PurchaseOrders.selectedPurchaseOrder;
export const selectPurchaseOrderLoading = (state: any) => state.PurchaseOrders.loading;
export const selectPurchaseOrderError = (state: any) => state.PurchaseOrders.error;

export default purchaseOrderSlice.reducer;
